import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import logo from "../../assets/logo.png";
import signUpLayer from "../../assets/sign_up_layer.png";
import Loader from "../../components/Loader";
import { verifyOtp, resendOtp } from "../../api/auth";
import { useNavigate } from "react-router-dom";

// ✅ Validation Schema
const schema = yup.object().shape({
  otp: yup
    .string()
    .required("OTP is required")
    .length(6, "OTP must be 6 digits"),
});

type FormData = {
  otp: string;
};

const VerifyOtp = () => {
  const navigate = useNavigate();
  const email = localStorage.getItem("email");
  const [otpArray, setOtpArray] = useState(Array(6).fill(""));
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const [isResending, setIsResending] = useState(false);

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  // ✅ OTP Input Logic
  const handleChange = (value: string, index: number) => {
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otpArray];
      newOtp[index] = value;
      setOtpArray(newOtp);
      setValue("otp", newOtp.join(""));

      if (value && index < 5) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData("text").slice(0, 6).split("");
    if (pasteData.every((c) => /^[0-9]$/.test(c))) {
      const newOtp = Array(6).fill("");
      pasteData.forEach((char, i) => {
        newOtp[i] = char;
      });
      setOtpArray(newOtp);
      setValue("otp", newOtp.join(""));
    }
  };

  // ✅ Submit
  const onSubmit = async (data: FormData) => {
    if (!email) {
      toast.error("Email not found, please login again!");
      return;
    }
    try {
      const payload = {
        otp: data.otp,
        email: email,
      };
      const res = await verifyOtp(payload);
      toast.success(res.data.message || "OTP Verified Successfully!");
      // redirect to reset password form
      navigate("/reset-password");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Invalid OTP, try again!");
    }
  };

  // ✅ Resend OTP

  const handleResend = async () => {
    // Clear OTP fields first
    setOtpArray(Array(6).fill(""));
    setValue("otp", "");
    inputsRef.current[0]?.focus();
    
    try {
      setIsResending(true); // loader start
      const email = localStorage.getItem("email");
      if (!email) {
        toast.error("Email not found!");
        return;
      }

      const res = await resendOtp({ email });
      toast.success(res.data.message || "OTP resent successfully!");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to resend OTP");
    } finally {
      setIsResending(false); // loader stop
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-center bg-cover p-6"
      style={{ backgroundImage: `url(${signUpLayer})` }}
    >
      <ToastContainer position="top-right" autoClose={3000} />
      {/* ✅ Logo above card */}
      <div className="flex justify-center mb-3">
        <img src={logo} alt="Logo" className="h-24 w-auto object-contain" />
      </div>
      <div className="bg-white w-full max-w-md rounded-2xl shadow-lg p-6 text-center">
        <h2 className="brand-heading mb-6">Verify OTP</h2>

        {isSubmitting && <Loader />}
        {isResending && <Loader />}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* OTP Fields */}
          <div className="flex justify-between">
            {otpArray.map((digit, i) => (
              <input
                key={i}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onPaste={handlePaste}
                disabled={isResending || isSubmitting}
                ref={(el) => {
                  inputsRef.current[i] = el!;
                }}
                className="w-12 h-12 text-center text-xl border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              />
            ))}
          </div>
          {errors.otp && (
            <p className="text-red-500 text-sm mt-1">{errors.otp.message}</p>
          )}

          {/* Verify Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#016683] text-white py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50"
          >
            {isSubmitting ? "Verifying..." : "Verify OTP"}
          </button>
        </form>

        {/* Resend OTP */}
        <p className="mt-6 text-sm text-gray-600">
          Didn’t get OTP?{" "}
          <button
            onClick={handleResend}
            disabled={isResending}
            className="text-blue-600 hover:underline disabled:opacity-50"
          >
            {isResending ? "Resending..." : "Resend OTP"}
          </button>
        </p>

        {/* Back to login */}
        <p className="text-sm text-center mt-4">
          <Link to="/login" className="text-blue-600 hover:underline">
            Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default VerifyOtp;
